import { useDeviceStatusLoading } from 'features/Integrations/hooks/useDeviceStatusLoading';
import { FaCircle, FaSpinner } from 'react-icons/fa';
import { getDeviceStatusCode } from 'utils/devices/icon.utils';
import styles from './NodeStatusIcon.module.css';

/**
 * The dot reads like the map pin: GREEN running normally, YELLOW the water is
 * up or the device wants attention, RED alerting, GREY offline or unknown.
 *
 * Warning, Rising and Dropping used to share a grey "neutral" bucket, so a
 * gauge the map drew yellow showed the same grey here as one that was offline.
 */
const STATUS_GROUPS = {
  online: ['online', 'open', 'idle', 'active'],
  warning: ['warning', 'maintenance', 'slow', 'rising', 'dropping', 'minor', 'action'],
  alert: ['alerting', 'closed', 'flooding', 'major', 'moderate'],
  offline: ['offline', 'inactive', 'api error'],
};

/** Anything unlisted is grey — an unknown state is not a healthy one. */
const getStatusVariant = (status) => {
  const normalized = (status || 'offline').toLowerCase();
  return (
    Object.keys(STATUS_GROUPS).find((variant) => STATUS_GROUPS[variant].includes(normalized)) ??
    'offline'
  );
};

export default function NodeStatusIcon({ device, size = 10 }) {
  // Called before the `!device` bail-out: a hook may not sit behind a conditional return.
  const isLoading = useDeviceStatusLoading(device);

  if (!device) return null;

  // No point yet and the covering fetch still in flight: a spinner, not a grey dot that
  // would read as Offline.
  if (isLoading) {
    return (
      <span className={styles.root} title="Loading">
        <FaSpinner className={styles.loading} style={{ fontSize: size }} />
      </span>
    );
  }

  // Use the same display rule as NodeStatus: no data means Offline.
  const displayStatus = device.data ? getDeviceStatusCode(device) : 'Offline';
  const variant = getStatusVariant(displayStatus);

  return (
    <span className={styles.root} title={displayStatus || 'Offline'}>
      <FaCircle className={styles[variant]} style={{ fontSize: size }} />
    </span>
  );
}
